import { useEffect, useMemo, useRef, useState } from "react";
import {
  errorSummary,
  fieldDomId,
  findFieldElement,
  focusFirstInvalid,
  mapServerIssues,
  orderedFieldErrors,
  type FieldSpec,
} from "@/lib/form-errors";
import { Link, useSearchParams } from "react-router-dom";
import { openMeetingId, patientHref } from "@/lib/patient-links";
import {
  BLOOD_TEST_STATUS_LABELS,
  FOLLOW_UP_ITEM_LABELS,
  followUpAfterLabAnswer,
  meetingOrderPrefill,
  prescriptionLabel,
  PUBLISH_DECISION_AND_SEND_LINK_CTA,
  PLANNED_MEDICATION_FIELD_LABEL,
  PLANNED_TREATMENT_CHOICE_LABEL,
  PLANNED_TREATMENT_CHOICES,
  orderMatchesPlan,
  plannedOrderProse,
  PUBLISH_TO_PATIENT_CTA,
  TREATMENT_DECISION_NOTE_TITLE,
  TREATMENT_DECISION_OUTCOME_LABEL,
  TREATMENT_DECISION_OUTCOMES,
  assembleTreatmentDecisionNoteText,
  finalizeDoctorNotice,
  finalizeTreatmentComment,
  treatmentCommentPrefill,
  type ClinicalPhase,
  type TreatmentDecisionNote,
  type ClinicianBloodTest,
  type LabSampleCost,
  type PlannedMedicationView,
  type PlannedTreatmentChoice,
  type PublishTreatmentDecision,
  type PublishTreatmentDecisionResult,
  type PublishTreatmentStatusDecisionResult,
  type TreatmentDecisionOutcomeChoice,
  treatmentStatusOutcomesFor,
} from "@care-by-ella/contracts";
import {
  fetchTreatmentDecisionNote,
  finalizeTreatmentDecisionNote,
  publishTreatmentDecision,
  UnauthorizedError,
  ValidationError,
  type PatientDetail,
} from "@/lib/clinician-api";
import { fetchPlannedMedication } from "@/lib/treatment-start-api";
import {
  decisionNoteCopyBlocker,
  decisionNoteCopyCurrent,
  decisionNoteCopyKey,
  decisionNoteDraft,
} from "@/lib/decision-note";
import { copyPendingText, selectElementText } from "@/lib/clipboard";
import { errorText } from "@/lib/error-text";
import {
  decisionNoticePrefill,
  doctorNoticeClaimErrors,
  doctorNoticeText,
} from "@/lib/doctor-notice";
import { DoctorNoticeField } from "./DoctorNoticeField";
import { TreatmentCommentField } from "./TreatmentCommentField";
import { decisionCommentOutcome } from "@/lib/treatment-comment";
import { ClinicalPhaseField } from "./ClinicalPhaseField";
import { PlannedMedicationSummary } from "./PlannedMedicationSummary";
import { OverviewCompletionRequest } from "./OverviewCompletionRequest";
import {
  EMPTY_PRESCRIPTION_CHOICE,
  NEW_APPROVAL_CHOICES,
  prescriptionDecisionPayload,
  WebdocControlStep,
  type PrescriptionChoiceValue,
} from "./WebdocControlStep";
import {
  CompletionPicker,
  completionPayload,
  completionProblem,
  type CompletionValue,
} from "./CompletionPicker";
import {
  EMPTY_ORDER,
  ORDER_FIELD_LABELS,
  orderDraftFrom,
  orderFieldErrors,
  orderTouched,
  PrescriptionOrderFields,
  toOrder,
  type OrderDraft,
} from "./PrescriptionOrderFields";
import {
  EMPTY_PLAN_CHOICE,
  planChoicePayload,
  SubscriptionPlanFields,
  type PlanChoiceValue,
} from "./SubscriptionPlanChoice";
import { hiddenWhenReadOnly } from "./SupportView";
import { TreatmentStatusChain } from "./TreatmentStatusChain";
import { TreatmentStatusDecisionEditor } from "./TreatmentStatusDecisionEditor";
import { requisitionRefControl } from "@/lib/requisition-ref";

// TES-952 (TES-941 §WEBDOC OCH BEHANDLINGSBESLUT) — the ONE workspace:
// "Lämna svar till patienten och fatta behandlingsbeslut".
//
// Emelie + Kirill 2026-09-25:
//   "Det ska inte finnas ett separat steg där Kirill först markerar provsvaret som granskat."
// Kirill reads the result in Webdoc (there is no integration), then does
// everything here: the answer to the patient, the plan and order, and one of
// three outcomes. Publishing writes the review stamp implicitly (Ö-242's
// assumption (a)); the server does it in the same transaction as the
// decision, so nothing is half-published if any part is refused.
//
// It replaced four controls on the patient page: "Provsvar har kommit in"
// (TES-506), "Provsvar granskat" with its three outcomes (TES-890),
// "Godkänn"/"Avslå" in the header, and the header's free-standing subscription
// link, whose plan choice (TES-907) now sits inside the approval.

const SAMPLE_COST_LABEL: Record<LabSampleCost, string> = {
  covered: "Omprov ingår — ingen ny betalning",
  charged: "Nytt/utökat provpaket — patienten betalar",
};

const COMPLETION_START: CompletionValue = {
  outcome: "completion",
  items: [],
  otherText: "",
  otherHeading: "",
  otherUploadAllowed: false,
};

function fmt(date: string) {
  return new Date(date).toLocaleString("sv-SE", {
    dateStyle: "short",
    timeStyle: "short",
  });
}

// TES-1031 — the form's fields in screen order: what the error summary names
// and where "scroll to the first invalid field" starts.
const FORM = "treatment-decision";
const DECISION_FIELDS: readonly FieldSpec[] = [
  { key: "summary", label: "Läkarens besked till patienten" },
  { key: "outcome", label: "Beslut" },
  { key: "medication", label: ORDER_FIELD_LABELS.medication },
  { key: "dose", label: ORDER_FIELD_LABELS.dose },
  { key: "frequency", label: ORDER_FIELD_LABELS.frequency },
  { key: "duration", label: ORDER_FIELD_LABELS.duration },
  // TES-1292 — a deviation from the approved medication catalogue.
  { key: "deviationReason", label: ORDER_FIELD_LABELS.deviationReason },
  { key: "plannedTreatment", label: "Planerad läkemedelsbehandling" },
  { key: "clinicalPhase", label: "Klinisk behandlingsfas" },
  { key: "followUp", label: "Medicinsk uppföljning" },
  { key: "plan", label: "Abonnemangslänk" },
  { key: "completion", label: "Komplettering" },
  { key: "sampleCost", label: "Provkostnad" },
  { key: "declineReason", label: "Motivering" },
  { key: "carePath", label: "Fortsatt vårdväg" },
  { key: "webdoc", label: "Kontroll i Webdoc" },
];

// Where the server's refusal paths land in the form.
const SERVER_PATHS: Record<string, string> = {
  patientSummary: "summary",
  outcome: "outcome",
  "approval.medication": "medication",
  "approval.dose": "dose",
  "approval.frequency": "frequency",
  "approval.duration": "duration",
  "approval.clinicalPhase": "clinicalPhase",
  "approval.withFollowUpRemaining": "followUp",
  "approval.prescription": "webdoc",
  prescription: "webdoc",
  plannedTreatment: "plannedTreatment",
  subscription: "plan",
  completion: "completion",
  sampleCost: "sampleCost",
  reason: "declineReason",
  carePath: "carePath",
  webdocControl: "webdoc",
  noteRevisionId: "webdoc",
};

function FieldErrorText({ message }: { message: string | undefined }) {
  return message ? <p className="text-xs text-danger">{message}</p> : null;
}

// The lab order the decision answers: the case's own order (TES-942), once
// her payment is verified and while no doctor has approved it. An unpaid case
// has no sample and so no result to answer; an approved one is answered.
export function labOrderToAnswer(
  bloodTest: ClinicianBloodTest | null | undefined,
): string | null {
  if (!bloodTest?.orderId) return null;
  if (bloodTest.status === "awaiting_payment") return null;
  if (bloodTest.reviewedAt && bloodTest.reviewOutcome === "approved")
    return null;
  return bloodTest.orderId;
}

function TreatmentDecisionEditor({
  patientId,
  data,
  bloodTest,
  onClose,
  onPublished,
  onUnauthorized,
}: {
  patientId: string;
  data: PatientDetail;
  bloodTest: ClinicianBloodTest | null | undefined;
  onClose: () => void;
  onPublished: (result: PublishTreatmentDecisionResult) => void;
  onUnauthorized: () => void;
}) {
  const [searchParams] = useSearchParams();
  const labOrderId = labOrderToAnswer(bloodTest);
  const answersLab = labOrderId !== null;

  // TES-1201 — "Läkarens besked till patienten · visas under Din vård".
  // Null until the doctor types in it: the text then follows the prefill,
  // so the outcome, the completions and a ticked remiss shape it.
  const [notice, setNotice] = useState<string | null>(null);
  // TES-1290 — an approval's text is "Läkarens kommentar till
  // behandlingsbeslutet" instead, prefilled per kind (approve, or after
  // kompletteringar kept / changed). Null until the doctor types in it.
  const [comment, setComment] = useState<string | null>(null);
  // Nothing chosen: every outcome is a clinical decision, none a default.
  const [outcome, setOutcome] = useState<TreatmentDecisionOutcomeChoice | null>(
    null,
  );
  const [order, setOrder] = useState<OrderDraft>(EMPTY_ORDER);
  const [clinicalPhase, setClinicalPhase] = useState<ClinicalPhase | null>(
    null,
  );
  const [followUpConfirmed, setFollowUpConfirmed] = useState(false);
  // After a 409 follow_up_remaining the server's list is the truth, so the
  // page's list is shown unfiltered and has to be confirmed again.
  const [showFullFollowUp, setShowFullFollowUp] = useState(false);
  const [plan, setPlan] = useState<PlanChoiceValue>(EMPTY_PLAN_CHOICE);
  const [completion, setCompletion] =
    useState<CompletionValue>(COMPLETION_START);
  const [sampleCost, setSampleCost] = useState<LabSampleCost | null>(null);
  const [internalReason, setInternalReason] = useState("");
  const [declineReason, setDeclineReason] = useState("");
  // TES-1061 (TES-1069 point 13) — journaled with the motivation.
  const [carePath, setCarePath] = useState("");
  // TES-1061 (TES-1069 point 1) — the note the decision is journaled in, as
  // the server names it, and the version "Kopiera" stored.
  const [decisionNote, setDecisionNote] =
    useState<TreatmentDecisionNote | null>(null);
  const [copied, setCopied] = useState<{
    text: string;
    revisionId: string;
    // TES-1413 — the decision and note it was copied from (decisionNoteCopyKey).
    key: string;
    clipboard: boolean;
  } | null>(null);
  const [lapsed, setLapsed] = useState(false);
  const [copying, setCopying] = useState(false);
  // TES-1185 (TES-1180 points 1, 2 and 4) — a positive decision carries its
  // prescription status, A-02 2026-10-08: "bekräftar separat
  // Journalanteckningen är införd i Webdoc och Receptet är utfärdat i Webdoc.
  // Därefter publiceras det positiva behandlingsbeslutet". It replaces
  // TES-1071's Steg B ticks and the separate "Bekräfta receptstatus" step.
  // Nothing preselected.
  const [prescription, setPrescription] = useState<PrescriptionChoiceValue>(
    EMPTY_PRESCRIPTION_CHOICE,
  );
  // TES-1185 (TES-1180 points 1 and 4) — after kompletteringar, the plan
  // saved at the meeting, kept or changed. Nothing preselected.
  const [plannedChoice, setPlannedChoice] = useState<
    PlannedTreatmentChoice["choice"] | null
  >(null);
  const [planReason, setPlanReason] = useState("");
  // TES-1221 (A-02 rules 2 and 5) — a change may set "styrka" and
  // "behandlingsplan" too, prefilled from the current version.
  const [planStrength, setPlanStrength] = useState("");
  const [planText, setPlanText] = useState("");
  const [labOrderDone, setLabOrderDone] = useState(false);
  // TES-442 — "Remissnummer i Webdoc (valfritt)" beside the remiss tick.
  const [requisitionRef, setRequisitionRef] = useState("");
  // TES-1055 (Emelie, point H) — the journal tick, on every outcome:
  // "Journalanteckningen är införd i Webdoc".
  // The API refuses the publish without it (400 journal_not_in_webdoc).
  const [journalDone, setJournalDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // TES-1031 — errors show at their fields once the doctor has tried to
  // publish; the server's field refusals show until she edits the form.
  const [attempted, setAttempted] = useState(false);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const [focusTick, setFocusTick] = useState(0);

  // TES-956 — the order fields are pre-filled once from the meeting's
  // structured order (TES-691's parsed free-text note only for a meeting
  // documented before it), never over what the doctor has typed.
  const prefill = meetingOrderPrefill(data.meetings ?? []);
  // TES-1185 — the planned medication's current version (and history), the
  // record "Planen kvarstår" keeps. Null while loading or for a patient
  // with no plan; the meeting prefill above stays the fallback.
  const [planned, setPlanned] = useState<PlannedMedicationView | null>(null);
  useEffect(() => {
    let live = true;
    fetchPlannedMedication(patientId)
      .then((v) => {
        if (live) setPlanned(v);
      })
      .catch((e) => {
        if (live && e instanceof UnauthorizedError) onUnauthorized();
      });
    return () => {
      live = false;
    };
    // onUnauthorized is a fresh callback each render; the plan is per patient.
  }, [patientId]);
  const currentPlan = planned?.current ?? null;
  const prefillKey = prefill ? JSON.stringify(prefill.order) : null;
  const [prefilled, setPrefilled] = useState(false);
  useEffect(() => {
    if (outcome !== "approve" || prefilled || !prefill) return;
    setPrefilled(true);
    setOrder((cur) =>
      orderTouched(cur) ? cur : orderDraftFrom(prefill.order),
    );
    // prefillKey stands in for `prefill`, which is a new object every render.
  }, [outcome, prefilled, prefillKey]);

  // TES-736 — follow-up warns, never blocks; the doctor confirms it.
  const followUp = showFullFollowUp
    ? (data.followUpOutstanding ?? [])
    : followUpAfterLabAnswer(data.followUpOutstanding ?? [], answersLab);
  const followUpRemaining = followUp.length > 0;

  const newSample = completion.items.includes("new_blood_test");
  const prescriptionPayload = prescriptionDecisionPayload(prescription);
  // The positive decision's choice as the publish sends it (never not_yet).
  const decisionPrescriptionChoice =
    prescriptionPayload && prescriptionPayload.choice !== "not_yet"
      ? {
          choice: prescriptionPayload.choice,
          ...(prescriptionPayload.existingValidUntil
            ? { existingValidUntil: prescriptionPayload.existingValidUntil }
            : {}),
        }
      : null;
  // TES-1071 (Simon) — the link goes by e-mail only, so the server refuses a
  // positive decision for a patient without a verified address.
  const emailVerified = data.patient.emailStatus === "verified";
  // TES-1186 — a komplettering with no result to answer goes through the
  // overview's request instead of this publish.
  const completionWithoutResult = outcome === "completion" && !answersLab;
  // The remiss line is part of the step only when a new sample is asked for.
  const needsLabOrder = outcome === "completion" && newSample;
  // TES-1201 — the notice's prefill reads only what is confirmed.
  // TES-1185 — "Receptet är utfärdat i Webdoc" confirms the prescription,
  // but she is told only once her subscription is active (A-02: nothing
  // reveals the prescription before subscription_activated).
  const subscriptionEntitled =
    data.subscription !== undefined &&
    data.subscription.standing !== "inactive";
  const noticeConfirmed = {
    labOrder: needsLabOrder && labOrderDone,
    prescription:
      outcome === "approve" &&
      decisionPrescriptionChoice?.choice === "new_issued" &&
      subscriptionEntitled,
  };
  const noticePrefill = decisionNoticePrefill(
    outcome,
    completion,
    noticeConfirmed,
  );
  const noticeText = doctorNoticeText(notice, noticePrefill);
  const planPayload = planChoicePayload(plan);

  // TES-1061 — the note, loaded once per dialog.
  useEffect(() => {
    let live = true;
    fetchTreatmentDecisionNote(patientId)
      .then((n) => {
        if (live) setDecisionNote(n);
      })
      .catch((e) => {
        if (!live) return;
        if (e instanceof UnauthorizedError) onUnauthorized();
        else setError(errorText(e instanceof Error ? e.message : String(e)));
      });
    return () => {
      live = false;
    };
    // onUnauthorized is a fresh callback each render; the note is per patient.
  }, [patientId]);

  const noteTarget = decisionNote?.target ?? null;
  // Offered when the decision follows kompletteringar (it is journaled in
  // the Kompletteringsanteckning) and the meeting saved a structured plan:
  // A-02's "när kompletteringarna är bedömda". The API requires the choice
  // then (TES-1221).
  const offerPlanChoice =
    outcome === "approve" &&
    (currentPlan !== null || prefill?.source === "structured") &&
    noteTarget?.kind === "completion_note";
  // TES-1221 — without kompletteringar there is no "Planen kvarstår"; the
  // plan only prefills the order. An order edited away from it is a change
  // of the plan, saved with its reason (the API refuses it otherwise).
  const planDiffers =
    outcome === "approve" &&
    !offerPlanChoice &&
    currentPlan !== null &&
    !orderMatchesPlan(toOrder(order), currentPlan);
  // The change starts from the current strength and behandlingsplan.
  useEffect(() => {
    if (!planDiffers) return;
    setPlanStrength(currentPlan?.strength ?? "");
    setPlanText(currentPlan?.plan ?? "");
    // Only when the order starts to differ; currentPlan is loaded by then.
  }, [planDiffers]);
  const effectivePlanChoice: PlannedTreatmentChoice["choice"] | null =
    offerPlanChoice ? plannedChoice : planDiffers ? "changed" : null;
  const plannedTreatment: PlannedTreatmentChoice | null =
    effectivePlanChoice === null
      ? null
      : effectivePlanChoice === "kept"
        ? { choice: "kept" }
        : {
            choice: "changed",
            reason: planReason.trim(),
            strength: planStrength.trim(),
            plan: planText.trim(),
          };
  // TES-1290 — the approval's comment; the other outcomes keep the notice
  // (TES-1311: no comment on a komplettering; Ö-480: a decline's
  // "Motivering" and "Fortsatt vårdväg" stay as they are until answered).
  // "Kept" and "changed" are the plan choice after kompletteringar only.
  const commentOutcome = decisionCommentOutcome(
    outcome,
    offerPlanChoice ? plannedChoice : null,
  );
  const commentPrefill = treatmentCommentPrefill(commentOutcome);
  const summary = commentOutcome
    ? doctorNoticeText(comment, commentPrefill)
    : noticeText;
  const noticeClaims = doctorNoticeClaimErrors(summary, noticeConfirmed);
  // What is published: the comment as finalized, the notice as typed (the
  // API drops its empty headings).
  const publishedText = commentOutcome
    ? finalizeTreatmentComment(summary)
    : summary.trim();
  // Prefilled headings alone are no answer to a lab result.
  const summaryMissing =
    answersLab &&
    !(commentOutcome ? publishedText : finalizeDoctorNotice(summary));
  const noteDraft = decisionNoteDraft({
    outcome,
    completion:
      outcome === "completion" && !completionProblem(completion)
        ? completionPayload(completion)
        : null,
    completionReason: newSample ? internalReason : "",
    declineReason,
    carePath,
    prescription: decisionPrescriptionChoice,
    plannedTreatment:
      plannedTreatment?.choice === "changed" && !plannedTreatment.reason
        ? null
        : plannedTreatment,
    plannedOrderText: plannedOrderProse({
      ...toOrder(order),
      strength: planStrength.trim(),
    }),
  });
  const noteDraftKey = JSON.stringify(noteDraft);
  // The note as it reads on screen now, exactly as the server compiles it.
  const liveNote = useMemo(
    () =>
      noteTarget && noteDraft
        ? assembleTreatmentDecisionNoteText(noteTarget, noteDraft)
        : null,
    // noteDraftKey stands in for noteDraft, a new object every render.
    [noteTarget, noteDraftKey],
  );
  // TES-1413 — the copy is current while the decision and its note are the
  // ones copied; the text on screen after a copy is the server's.
  const noteCopyKey = decisionNoteCopyKey(noteTarget, noteDraft);
  const copyCurrent = decisionNoteCopyCurrent(copied?.key ?? null, noteCopyKey);
  const shownNote = copied && copyCurrent ? copied.text : liveNote;
  // TES-1413 — the note is copied once it says everything it will say.
  const copyBlocker = decisionNoteCopyBlocker({
    outcome,
    prescriptionChosen: decisionPrescriptionChoice !== null,
    planChoiceNeeded: offerPlanChoice,
    planChoice: effectivePlanChoice,
    planReason,
  });
  // Without a note to write into (no documented meeting, no completion
  // round) the tick stands alone, as before.
  const journalLocked = noteTarget !== null && !copyCurrent;

  // TES-1063 pattern — the decision changed after the note was copied: the
  // tick no longer covers it. Taken back now, not when he publishes.
  useEffect(() => {
    if (copied && !copyCurrent) {
      setJournalDone(false);
      setLapsed(true);
    }
  }, [copied, copyCurrent]);

  // TES-1413 — a copy the browser refused leaves the note selected, so
  // Cmd+C finishes it; the tick unlocks either way.
  const noteTextRef = useRef<HTMLPreElement>(null);
  useEffect(() => {
    if (copied && !copied.clipboard) selectElementText(noteTextRef.current);
  }, [copied]);

  async function copyNote() {
    if (!noteDraft || !noteCopyKey || copyBlocker) return;
    const key = noteCopyKey;
    setCopying(true);
    setError(null);
    // Both started inside the click: Safari refuses a clipboard write begun
    // after an await. The server answers an unchanged note with the version
    // it already stored.
    const finalized = finalizeTreatmentDecisionNote(patientId, noteDraft);
    const clipboard = copyPendingText(finalized.then((f) => f.text));
    try {
      const fin = await finalized;
      setCopied({
        text: fin.text,
        revisionId: fin.revisionId,
        key,
        clipboard: await clipboard,
      });
      setLapsed(false);
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      setError(errorText(e instanceof Error ? e.message : String(e)));
    } finally {
      setCopying(false);
    }
  }

  // TES-1031 — what each field says while the decision cannot be published,
  // in the doctor's words. Replaces the single `problem` line that named only
  // the first missing thing, under a button that could not be pressed.
  const localErrors: Record<string, string | undefined> = (() => {
    const e: Record<string, string | undefined> = {};
    if (summaryMissing)
      e.summary = commentOutcome
        ? "Skriv kommentaren till patienten."
        : "Skriv beskedet till patienten.";
    else if (noticeClaims.length > 0) e.summary = noticeClaims[0];
    if (outcome === null) e.outcome = "Välj ett beslut.";
    if (outcome === "approve") {
      Object.assign(e, orderFieldErrors(order));
      if (!clinicalPhase) e.clinicalPhase = "Välj klinisk behandlingsfas.";
      if (followUpRemaining && !followUpConfirmed)
        e.followUp = "Bekräfta att uppföljningen får vara kvar.";
      if (!emailVerified)
        e.plan =
          "Patienten saknar verifierad e-postadress. Abonnemangslänken skickas bara via e-post.";
      else if (!planPayload)
        e.plan =
          plan.plan === null
            ? "Välj abonnemangslänk."
            : "Ange underlaget för tolv månaders behandling.";
      if (!decisionPrescriptionChoice)
        e.webdoc = "Bekräfta att e-receptet är utfärdat i Webdoc.";
      if (offerPlanChoice && plannedChoice === null)
        e.plannedTreatment =
          "Välj om planen kvarstår eller om den planerade läkemedelsbehandlingen ska ändras.";
      else if (effectivePlanChoice === "changed" && !planReason.trim())
        e.plannedTreatment = "Ange den medicinska motiveringen till ändringen.";
    }
    if (outcome === "completion") {
      const p = completionProblem(completion);
      if (p) e.completion = p;
      if (newSample && sampleCost === null)
        e.sampleCost = "Välj om det nya provet ingår eller betalas.";
      if (needsLabOrder && !labOrderDone)
        e.webdoc =
          "Bekräfta att provbeställningen/remissen är skapad i Webdoc.";
    }
    if (outcome === "decline" && !declineReason.trim())
      e.declineReason = "Ange en motivering.";
    // TES-1185 — the journal tick on every outcome, the approval included.
    if (outcome !== null && !journalDone)
      e.webdoc = "Bekräfta att journalanteckningen är införd i Webdoc.";
    // TES-1061 — the tick covers the copied note only; first things first.
    // TES-1413 — and what the note still waits for, before the copy.
    if (outcome !== null && noteTarget && !copyCurrent)
      e.webdoc =
        copyBlocker ??
        "Kopiera journalanteckningen med beslutet och för in den i Webdoc.";
    return e;
  })();
  const fieldErrors = orderedFieldErrors(DECISION_FIELDS, {
    ...(attempted ? localErrors : {}),
    ...serverErrors,
  });
  const shownError = (key: string) =>
    fieldErrors.find((f) => f.key === key)?.message;
  const summaryLine = errorSummary(fieldErrors);

  // Any edit clears the server's refusals: they described the request that
  // was sent, not the form as it stands now.
  useEffect(() => {
    setServerErrors({});
  }, [
    summary,
    outcome,
    order,
    clinicalPhase,
    followUpConfirmed,
    plan,
    completion,
    sampleCost,
    declineReason,
    carePath,
    prescription,
    plannedChoice,
    planReason,
    planStrength,
    planText,
    labOrderDone,
    journalDone,
  ]);

  // Scroll to and focus the first invalid field after the render that shows
  // the errors (a server refusal only exists after that render).
  useEffect(() => {
    if (focusTick === 0) return;
    focusFirstInvalid(fieldErrors, (key) =>
      findFieldElement(document, FORM, key),
    );
    // Runs once per request, not on every change of the error list.
  }, [focusTick]);

  // Whether anything still stands in the way of publishing.
  const blocked = Object.values(localErrors).some(Boolean);

  function body(): PublishTreatmentDecision | null {
    const common = {
      ...(labOrderId ? { labOrderId } : {}),
      ...(publishedText ? { patientSummary: publishedText } : {}),
      webdocControl: {
        journalInWebdoc: journalDone,
        ...(needsLabOrder ? { labOrderDoneInWebdoc: labOrderDone } : {}),
        ...requisitionRefControl({
          needsLabOrder,
          labOrderDone,
          requisitionRef,
        }),
      },
      // TES-1061 — the copied version the tick is a receipt on.
      ...(copied && copyCurrent ? { noteRevisionId: copied.revisionId } : {}),
    };
    if (
      outcome === "approve" &&
      clinicalPhase &&
      planPayload &&
      decisionPrescriptionChoice
    ) {
      return {
        ...common,
        webdocControl: { journalInWebdoc: journalDone },
        // TES-1185 — registered with the decision, released by the
        // verified subscription.
        prescription: decisionPrescriptionChoice,
        ...(plannedTreatment ? { plannedTreatment } : {}),
        outcome,
        approval: {
          ...toOrder(order),
          clinicalPhase,
          ...(followUpRemaining ? { withFollowUpRemaining: true } : {}),
        },
        subscription: planPayload,
      };
    }
    if (outcome === "completion") {
      return {
        ...common,
        outcome,
        completion: completionPayload(completion),
        ...(newSample && sampleCost ? { sampleCost } : {}),
        ...(newSample && internalReason.trim()
          ? { reason: internalReason.trim() }
          : {}),
      };
    }
    if (outcome === "decline") {
      return {
        ...common,
        outcome,
        reason: declineReason.trim(),
        ...(carePath.trim() ? { carePath: carePath.trim() } : {}),
      };
    }
    return null;
  }

  async function publish() {
    const decision = body();
    if (!decision || blocked) {
      setAttempted(true);
      setFocusTick((t) => t + 1);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      onPublished(await publishTreatmentDecision(patientId, decision));
    } catch (e) {
      if (e instanceof UnauthorizedError) {
        onUnauthorized();
        return;
      }
      if (e instanceof ValidationError) {
        // TES-1031 — the server refused named fields: mark them like the
        // form's own errors instead of printing the error code.
        const { messages, unmapped } = mapServerIssues(
          e.issues,
          SERVER_PATHS,
          (key) => localErrors[key] ?? null,
        );
        setServerErrors(messages);
        setAttempted(true);
        setFocusTick((t) => t + 1);
        setError(
          unmapped.length || !Object.keys(messages).length
            ? "Beslutet kunde inte publiceras. Kontrollera uppgifterna och försök igen."
            : null,
        );
        return;
      }
      const msg = e instanceof Error ? e.message : String(e);
      if (msg === "follow_up_remaining") {
        // TES-736 — something is still open after the lab answer. Nothing was
        // published (one transaction); show the whole list and ask again.
        setShowFullFollowUp(true);
        setFollowUpConfirmed(false);
        setError(
          "Patienten har medicinsk uppföljning kvar. Bekräfta och publicera igen.",
        );
        return;
      }
      if (
        msg === "decision_note_changed" ||
        msg === "decision_note_not_copied"
      ) {
        // TES-1061 — the note moved on (a box saved elsewhere, evidence in):
        // nothing was published. Show the note as it stands and ask again.
        setCopied(null);
        setJournalDone(false);
        try {
          setDecisionNote(await fetchTreatmentDecisionNote(patientId));
        } catch {
          // The refusal below is what matters; the old note stays shown.
        }
        setError(errorText(msg));
        return;
      }
      setError(msg);
    } finally {
      setBusy(false);
    }
  }

  // TES-997 (Ö-259) — one final CTA; what it publishes is the outcome chosen
  // above. TES-1071 (TES-1069 point 4) — a positive decision's button says
  // what it does: "Publicera behandlingsbeslut och skicka abonnemangslänk via e-post".
  const publishLabel = busy
    ? "Publicerar…"
    : outcome === "approve"
      ? PUBLISH_DECISION_AND_SEND_LINK_CTA
      : PUBLISH_TO_PATIENT_CTA;

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center bg-black/30 sm:items-center sm:p-md">
      {/* TES-966 — full screen on a phone, the whole panel scrolling. */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="treatment-decision-title"
        className="flex h-full w-full flex-col gap-md overflow-y-auto bg-surface p-md sm:h-auto sm:max-h-[90vh] sm:max-w-xl sm:rounded-md sm:p-lg"
      >
        <h3 id="treatment-decision-title" className="font-display text-xl">
          Lämna svar till patienten och fatta behandlingsbeslut
        </h3>
        {/* TES-955 (TES-941) — the patient's name is clickable in the review
            modes too. It closes the dialog and shows her overview; an open
            care meeting stays open (`?mote=` is kept). */}
        <p className="-mt-sm text-sm text-textSecondary">
          för{" "}
          <Link
            to={patientHref(patientId, "oversikt", openMeetingId(searchParams))}
            onClick={onClose}
            className="font-semibold text-brand hover:underline"
          >
            {data.patient.name ??
              `Patient …${data.patient.personalNumber.slice(-6)}`}
          </Link>
        </p>

        {/* TES-1291 — where she is in the chain, read-only. */}
        <TreatmentStatusChain
          startState={data.treatmentStartState}
          plannedMedication={currentPlan !== null}
        />

        {/* What is being answered. "Inkommet" and "granskat" are written when
            he publishes, so the dialog says so rather than asking for them. */}
        {answersLab && bloodTest ? (
          <div className="rounded-md border border-surfaceMuted p-sm text-sm">
            <p>
              Blodprov i det aktuella ärendet:{" "}
              <span className="font-semibold">
                {BLOOD_TEST_STATUS_LABELS[bloodTest.status]}
              </span>
              {bloodTest.sampleReportedAt
                ? ` · patienten markerade provet som lämnat ${fmt(bloodTest.sampleReportedAt)}`
                : ""}
            </p>
            <p className="mt-xs text-xs text-textSecondary">
              Läs provsvaret i Webdoc. När du publicerar registreras provsvaret
              som inkommet och granskat av dig, med det utfall beslutet ger.
            </p>
          </div>
        ) : (
          <p className="text-sm text-textSecondary">
            {bloodTest?.status === "awaiting_payment"
              ? "Blodprovet är inte betalt, så beslutet besvarar inget provsvar."
              : "Inget provsvar att besvara i det aktuella ärendet. Beslutet fattas på det underlag som finns."}
          </p>
        )}

        {commentOutcome ? (
          <TreatmentCommentField
            id={fieldDomId(FORM, "summary")}
            text={summary}
            value={comment}
            prefill={commentPrefill}
            onChange={setComment}
            structured={toOrder(order)}
            confirmed={noticeConfirmed}
            disabled={busy}
            error={noticeClaims.length > 0 ? undefined : shownError("summary")}
          />
        ) : (
          <DoctorNoticeField
            id={fieldDomId(FORM, "summary")}
            text={summary}
            value={notice}
            prefill={noticePrefill}
            onChange={setNotice}
            confirmed={noticeConfirmed}
            disabled={busy}
            // The field lists a claim it may not make itself.
            error={noticeClaims.length > 0 ? undefined : shownError("summary")}
          />
        )}

        <fieldset
          id={fieldDomId(FORM, "outcome")}
          className="flex flex-col gap-xs"
        >
          <legend className="mb-xs text-sm font-semibold">Beslut</legend>
          {/* TES-1186 (TES-1180 point 6) — a further komplettering is
              always on offer. It used to be disabled without a result to
              answer, with a hint pointing back to the meeting. Emelie:
              "Det ska vara möjligt att starta samma CP‑06-flöde … efter en tidigare komplettering, senare under ett aktivt medicinskt patientärende." */}
          {TREATMENT_DECISION_OUTCOMES.map((o) => (
            <label
              key={o}
              className={`flex cursor-pointer items-start gap-sm rounded-md border p-sm text-sm ${
                outcome === o
                  ? "border-brand bg-brand-accent"
                  : "border-surfaceMuted"
              }`}
            >
              <input
                type="radio"
                name={`treatment-decision-${patientId}`}
                className="mt-[3px] accent-brand"
                checked={outcome === o}
                disabled={busy}
                onChange={() => setOutcome(o)}
              />
              <span className="font-semibold">
                {TREATMENT_DECISION_OUTCOME_LABEL[o]}
              </span>
            </label>
          ))}
          <FieldErrorText message={shownError("outcome")} />
        </fieldset>

        {outcome === "approve" ? (
          <div className="flex flex-col gap-md">
            {prefill?.source === "structured" ? (
              <div className="rounded-md border border-brand/30 bg-brand/5 p-sm">
                <p className="mb-xs text-xs font-semibold text-textSecondary">
                  Ordination från vårdmötet {fmt(prefill.at)}
                </p>
                <p className="text-sm">
                  {prescriptionLabel({
                    medication: prefill.order.medication ?? null,
                    dose: prefill.order.dose ?? "",
                    frequency: prefill.order.frequency ?? null,
                    duration: prefill.order.duration ?? null,
                  })}
                </p>
                <p className="mt-xs text-xs text-textSecondary">
                  Fälten nedan är förifyllda med den. Kontrollera dem — de är
                  det patienten ser.
                </p>
              </div>
            ) : prefill?.source === "legacy_note" ? (
              <div className="rounded-md border border-brand/30 bg-brand/5 p-sm">
                <p className="mb-xs text-xs font-semibold text-textSecondary">
                  Från vårdmötet {fmt(prefill.at)}
                </p>
                <p className="whitespace-pre-wrap text-sm">{prefill.text}</p>
                <p className="mt-xs text-xs text-textSecondary">
                  Fälten nedan är förifyllda ur den här raden. Kontrollera dem —
                  de är det patienten ser, den här texten är det inte.
                </p>
                {prefill.multipleDoses ? (
                  <p className="mt-xs text-xs font-semibold text-warning">
                    Anteckningen nämner mer än en styrka. Startdosen är ifylld;
                    en eventuell trappning sätts som en dosjustering senare,
                    inte här.
                  </p>
                ) : null}
              </div>
            ) : null}
            {prefill?.doseLeftOut ? (
              // TES-1031 — the meeting's dose could not be sent as written
              // (no unit, or not a dose at all), so the field was left empty
              // rather than prefilled with a value the publish would refuse.
              <p className="text-xs font-semibold text-warning">
                Dosen från vårdmötet („{prefill.doseLeftOut}”) saknar giltig
                enhet och har inte fyllts i. Ange dos med enhet, t.ex. 0,25 mg.
              </p>
            ) : null}
            {offerPlanChoice || planDiffers ? (
              <fieldset
                id={fieldDomId(FORM, "plannedTreatment")}
                tabIndex={-1}
                className="flex flex-col gap-xs"
              >
                <legend className="mb-xs text-sm font-semibold">
                  Planerad läkemedelsbehandling
                </legend>
                {currentPlan ? (
                  <PlannedMedicationSummary
                    current={currentPlan}
                    history={planned?.versions ?? []}
                  />
                ) : null}
                {planDiffers ? (
                  // TES-1221 — no kompletteringar, so no "Planen kvarstår":
                  // the order differs from the plan, which makes it a change.
                  <p className="text-sm">
                    {PLANNED_TREATMENT_CHOICE_LABEL.changed}: ordinationen
                    skiljer sig från den planerade läkemedelsbehandlingen.
                  </p>
                ) : null}
                {(offerPlanChoice ? PLANNED_TREATMENT_CHOICES : []).map((c) => (
                  <label key={c} className="flex items-center gap-sm text-sm">
                    <input
                      type="radio"
                      name={`decision-planned-${patientId}`}
                      className="accent-brand"
                      checked={plannedChoice === c}
                      disabled={busy}
                      onChange={() => {
                        setPlannedChoice(c);
                        // Kept is the plan as saved, not as edited since.
                        if (c === "kept" && currentPlan)
                          setOrder(orderDraftFrom(currentPlan));
                        else if (
                          c === "kept" &&
                          prefill?.source === "structured"
                        )
                          setOrder(orderDraftFrom(prefill.order));
                        // TES-1221 — a change starts from the current
                        // strength and behandlingsplan.
                        if (c === "changed" && plannedChoice !== "changed") {
                          setPlanStrength(currentPlan?.strength ?? "");
                          setPlanText(currentPlan?.plan ?? "");
                        }
                      }}
                    />
                    {PLANNED_TREATMENT_CHOICE_LABEL[c]}
                  </label>
                ))}
                {effectivePlanChoice === "changed" ? (
                  <label className="flex flex-col gap-xs text-sm">
                    <span className="text-textSecondary">
                      Medicinsk motivering till ändringen
                    </span>
                    <textarea
                      value={planReason}
                      onChange={(e) => setPlanReason(e.target.value)}
                      rows={2}
                      disabled={busy}
                      className="rounded-md border border-surfaceMuted p-sm text-sm focus:border-brand focus:outline-none"
                    />
                  </label>
                ) : null}
                {/* TES-1221 (A-02 rules 2 and 5) — the two parts of the plan
                    the order fields below do not hold, labelled as in the
                    meeting's form. */}
                {effectivePlanChoice === "changed" ? (
                  <div className="flex flex-wrap items-start gap-sm">
                    <label className="flex w-40 flex-col gap-xs text-sm">
                      <span className="text-textSecondary">
                        {PLANNED_MEDICATION_FIELD_LABEL.strength}
                      </span>
                      <input
                        value={planStrength}
                        onChange={(e) => setPlanStrength(e.target.value)}
                        placeholder="t.ex. 2,4 mg"
                        maxLength={64}
                        disabled={busy}
                        className="rounded-md border border-surfaceMuted p-sm text-sm focus:border-brand focus:outline-none"
                      />
                    </label>
                    <label className="flex min-w-[16rem] flex-1 flex-col gap-xs text-sm">
                      <span className="text-textSecondary">
                        {PLANNED_MEDICATION_FIELD_LABEL.plan}
                      </span>
                      <textarea
                        value={planText}
                        onChange={(e) => setPlanText(e.target.value)}
                        rows={2}
                        maxLength={2000}
                        disabled={busy}
                        className="rounded-md border border-surfaceMuted p-sm text-sm focus:border-brand focus:outline-none"
                      />
                    </label>
                  </div>
                ) : null}
                <FieldErrorText message={shownError("plannedTreatment")} />
              </fieldset>
            ) : null}
            <PrescriptionOrderFields
              idPrefix="decision-order"
              formId={FORM}
              value={order}
              onChange={setOrder}
              disabled={
                busy || (offerPlanChoice && plannedChoice !== "changed")
              }
              errors={{
                medication: shownError("medication"),
                dose: shownError("dose"),
                frequency: shownError("frequency"),
                duration: shownError("duration"),
                deviationReason: shownError("deviationReason"),
              }}
            />
            <div id={fieldDomId(FORM, "clinicalPhase")} tabIndex={-1}>
              <ClinicalPhaseField
                idPrefix="decision"
                value={clinicalPhase}
                onChange={setClinicalPhase}
                disabled={busy}
              />
              <FieldErrorText message={shownError("clinicalPhase")} />
            </div>
            {followUpRemaining ? (
              <div
                id={fieldDomId(FORM, "followUp")}
                role="status"
                className="rounded-md border border-warning/60 bg-warning/5 p-sm"
              >
                <p className="text-sm font-semibold text-warning">
                  Medicinsk uppföljning kvar
                </p>
                <ul className="mt-xs list-disc pl-md text-sm">
                  {followUp.map((item) => (
                    <li key={item}>{FOLLOW_UP_ITEM_LABELS[item].sv}</li>
                  ))}
                </ul>
                <p className="mt-xs text-xs text-textSecondary">
                  Uppgifterna ligger kvar i patientens checklista efter
                  publiceringen och stängs när de är gjorda och granskade.
                </p>
                <label className="mt-sm flex items-start gap-sm text-sm">
                  <input
                    type="checkbox"
                    className="mt-[3px]"
                    checked={followUpConfirmed}
                    onChange={(e) => setFollowUpConfirmed(e.target.checked)}
                    disabled={busy}
                  />
                  <span>
                    Jag bekräftar att behandlingen aktiveras med medicinsk
                    uppföljning kvar
                  </span>
                </label>
                <FieldErrorText message={shownError("followUp")} />
              </div>
            ) : null}
            <div
              id={fieldDomId(FORM, "plan")}
              tabIndex={-1}
              className={`rounded-md border p-sm ${
                shownError("plan") ? "border-danger" : "border-surfaceMuted"
              }`}
            >
              <p className="mb-xs text-sm font-semibold">Abonnemangslänk</p>
              {/* TES-1071 (A-02) — "Kirill ska alltid se abonnemang, pris och källa till valet före publicering."
                  A new-treatment patient makes no subscription choice of her
                  own (that is F-07, transfers only), so the source is always
                  the doctor here. */}
              <p className="mb-xs text-xs text-textSecondary">
                Källa: läkarens val. Patienten har inget eget giltigt
                abonnemangsval.
              </p>
              <SubscriptionPlanFields
                patientId={patientId}
                value={plan}
                onChange={setPlan}
                disabled={busy}
                name={`decision-plan-${patientId}`}
              />
              <p className="mt-xs text-xs text-textSecondary">
                {emailVerified
                  ? "Länken skickas via e-post när beslutet publiceras. Patienten ser receptet och behandlingsstarten först när abonnemanget är aktiverat."
                  : "Patienten saknar verifierad e-postadress. Beslutet kan inte publiceras förrän hon har verifierat sin e-post."}
              </p>
              <FieldErrorText message={shownError("plan")} />
            </div>
          </div>
        ) : null}

        {/* TES-1186 — with no result to answer, the komplettering is the
            overview's "Begär komplettering" (TES-1169): the same CP-06 flow,
            joining the case she is working on, with its own remiss tick.
            Nothing of the decision is published, so the rest of this form
            does not apply. */}
        {completionWithoutResult ? (
          <OverviewCompletionRequest
            patientId={patientId}
            onUnauthorized={onUnauthorized}
            startOpen
            onSent={onClose}
          />
        ) : null}

        {outcome === "completion" && !completionWithoutResult ? (
          <div className="flex flex-col gap-sm">
            <div id={fieldDomId(FORM, "completion")} tabIndex={-1}>
              <CompletionPicker
                patientId={patientId}
                value={completion}
                onChange={setCompletion}
                showOutcome={false}
                disabled={busy}
                onUnauthorized={onUnauthorized}
              />
              <FieldErrorText message={shownError("completion")} />
            </div>
            {newSample ? (
              <>
                <p className="text-xs text-textSecondary">
                  Nytt prov — välj vad som gäller. Inget är förvalt.
                </p>
                <div
                  id={fieldDomId(FORM, "sampleCost")}
                  className="flex flex-col gap-xs"
                >
                  {(Object.keys(SAMPLE_COST_LABEL) as LabSampleCost[]).map(
                    (c) => (
                      <label
                        key={c}
                        className="flex items-center gap-xs text-xs"
                      >
                        <input
                          type="radio"
                          name={`decision-sample-cost-${patientId}`}
                          checked={sampleCost === c}
                          disabled={busy}
                          onChange={() => setSampleCost(c)}
                        />
                        {SAMPLE_COST_LABEL[c]}
                      </label>
                    ),
                  )}
                  <FieldErrorText message={shownError("sampleCost")} />
                </div>
                <label className="block text-xs text-textSecondary">
                  Skäl (valfritt, syns inte för patienten)
                  <textarea
                    value={internalReason}
                    onChange={(e) => setInternalReason(e.target.value)}
                    rows={2}
                    disabled={busy}
                    className="mt-xs w-full rounded-md border border-surfaceMuted p-xs text-xs"
                  />
                </label>
              </>
            ) : null}
          </div>
        ) : null}

        {outcome === "decline" ? (
          <label
            id={fieldDomId(FORM, "declineReason")}
            className="flex flex-col gap-xs text-sm"
          >
            <span className="text-textSecondary">
              Behandlingen avslutas och en respektfull notis skickas till
              patienten. Motiveringen visas för patienten under Din vård och
              journalförs med beslutet.
            </span>
            <textarea
              className="min-h-[80px] rounded-md border border-surfaceMuted p-sm text-sm focus:border-brand focus:outline-none"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              placeholder="T.ex. kontraindikation: pågående graviditet"
              disabled={busy}
            />
            <FieldErrorText message={shownError("declineReason")} />
          </label>
        ) : null}
        {outcome === "decline" ? (
          <label
            id={fieldDomId(FORM, "carePath")}
            className="flex flex-col gap-xs text-sm"
          >
            <span className="font-semibold">Fortsatt vårdväg (valfritt)</span>
            <textarea
              className="min-h-[60px] rounded-md border border-surfaceMuted p-sm text-sm focus:border-brand focus:outline-none"
              value={carePath}
              onChange={(e) => setCarePath(e.target.value)}
              disabled={busy}
            />
            <span className="text-xs text-textSecondary">
              Visas för patienten under Din vård och journalförs med beslutet.
            </span>
            <FieldErrorText message={shownError("carePath")} />
          </label>
        ) : null}

        {/* TES-1061 (TES-1069 point 1) — the note the decision is journaled
            in: the care meeting's, or with a completion the round's
            Kompletteringsanteckning. Only that note is copied, never an
            earlier one; the journal tick below covers the copied text. */}
        {outcome !== null && !completionWithoutResult && noteTarget ? (
          <div
            className="flex flex-col gap-sm rounded-md border border-surfaceMuted p-sm"
            data-testid="treatment-decision-note"
          >
            <p className="text-sm font-semibold">
              {TREATMENT_DECISION_NOTE_TITLE[noteTarget.kind]}
            </p>
            {decisionNote?.earlierNote ? (
              <details>
                <summary className="cursor-pointer text-xs text-textSecondary">
                  Tidigare anteckning: {decisionNote.earlierNote.title}
                  {decisionNote.earlierNote.locked ? " (låst)" : ""} — visas som
                  referens och kopieras inte
                </summary>
                <pre className="mt-xs max-h-64 overflow-y-auto whitespace-pre-wrap rounded-sm border border-surfaceMuted bg-surfaceMuted/40 p-sm text-xs text-textSecondary">
                  {decisionNote.earlierNote.text}
                </pre>
              </details>
            ) : null}
            {shownNote ? (
              <pre
                ref={noteTextRef}
                className="max-h-72 overflow-y-auto whitespace-pre-wrap rounded-sm border border-surfaceMuted p-sm text-xs"
                data-testid="treatment-decision-note-text"
              >
                {shownNote}
              </pre>
            ) : (
              <p className="text-xs text-textSecondary">
                Fyll i beslutet ovan för att se anteckningen.
              </p>
            )}
            <div className="flex flex-wrap items-center gap-sm">
              <button
                type="button"
                onClick={() => void copyNote()}
                disabled={busy || copying || !liveNote || copyBlocker !== null}
                aria-describedby={
                  copyBlocker ? "treatment-decision-copy-waits" : undefined
                }
                className="rounded-md border border-brand px-md py-sm text-sm text-brand disabled:opacity-50"
              >
                Kopiera journalanteckningen
              </button>
              {copied && copyCurrent ? (
                <span role="status" className="text-xs text-textSecondary">
                  {copied.clipboard
                    ? "Kopierad. För in den i Webdoc och bekräfta nedan."
                    : "Texten kunde inte kopieras automatiskt och är markerad ovan. Kopiera den med Cmd+C (Ctrl+C), för in den i Webdoc och bekräfta nedan."}
                </span>
              ) : copyBlocker && liveNote ? (
                <span
                  id="treatment-decision-copy-waits"
                  className="text-xs text-textSecondary"
                >
                  {copyBlocker}
                </span>
              ) : null}
            </div>
            {lapsed && !copyCurrent ? (
              <p role="status" className="text-xs text-danger">
                Anteckningen har ändrats efter kopieringen. Kopiera den igen och
                bekräfta att den är införd i Webdoc.
              </p>
            ) : null}
          </div>
        ) : null}

        {/* TES-997 — the control step, last before the publish: the
            prescription choice on an approval, the remiss on a new sample.
            TES-1055 — and the journal tick, on every outcome. */}
        {outcome !== null && !completionWithoutResult ? (
          <div id={fieldDomId(FORM, "webdoc")} tabIndex={-1}>
            <WebdocControlStep
              idPrefix="treatment-decision"
              journal={{ done: journalDone, onChange: setJournalDone }}
              {...(outcome === "approve"
                ? {
                    decisionPrescription: true,
                    // TES-1315 — a new approval issues a new e-recept.
                    decisionChoices: NEW_APPROVAL_CHOICES,
                    prescription,
                    onPrescriptionChange: setPrescription,
                  }
                : {})}
              labOrder={needsLabOrder}
              labOrderDone={labOrderDone}
              onLabOrderDoneChange={setLabOrderDone}
              requisitionRef={{
                value: requisitionRef,
                onChange: setRequisitionRef,
              }}
              disabled={busy}
              journalLocked={journalLocked}
            />
            <FieldErrorText message={shownError("webdoc")} />
          </div>
        ) : null}

        {completionWithoutResult ? null : (
          <p className="text-xs text-textSecondary">
            Beslutet publiceras för patienten och loggas med dig som signerande
            läkare.
          </p>
        )}

        {error ? (
          <p className="rounded-md border border-danger/30 bg-danger/10 p-sm text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="flex justify-end gap-sm">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-md px-md py-sm text-sm text-textSecondary hover:bg-surfaceMuted"
          >
            Avbryt
          </button>
          {completionWithoutResult ? null : (
            <button
              type="button"
              onClick={() => void publish()}
              disabled={busy}
              className={`rounded-md px-md py-sm text-sm text-white hover:opacity-90 disabled:opacity-60 ${
                outcome === "decline"
                  ? "bg-danger"
                  : "bg-brand text-textOnBrand"
              }`}
            >
              {publishLabel}
            </button>
          )}
        </div>
        {summaryLine && !busy && !completionWithoutResult ? (
          <p role="alert" className="-mt-sm text-right text-xs text-danger">
            {summaryLine}
          </p>
        ) : null}
      </div>
    </div>
  );
}

// TES-1291 (TES-1267 §4) — ONE workspace for every decision kind. A
// treatment that is already running (active, between cycles or paused) gets
// its outcomes (pause, resumption, end); onboarding gets the first
// decision's. Both open with the same status chain.
function DecisionWorkspace(
  props: Parameters<typeof TreatmentDecisionEditor>[0] & {
    onStatusPublished: (result: PublishTreatmentStatusDecisionResult) => void;
  },
) {
  const { onStatusPublished, ...rest } = props;
  if (treatmentStatusOutcomesFor(props.data.treatment?.state).length > 0) {
    return (
      <TreatmentStatusDecisionEditor
        patientId={props.patientId}
        data={props.data}
        onClose={props.onClose}
        onPublished={onStatusPublished}
        onUnauthorized={props.onUnauthorized}
      />
    );
  }
  return <TreatmentDecisionEditor {...rest} />;
}

// TES-1040 — hidden in the admin's read-only support view: the treatment decision is published to the patient.
export const TreatmentDecisionWorkspace = hiddenWhenReadOnly(DecisionWorkspace);
